import * as React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { isToday } from "date-fns";
import { MarkdownRenderer } from "@/components/feature/markdown/markdownRenderer";
import { TypingIndicator } from "./TypingIndicator";
import { Globe } from "lucide-react";
import { CopyButton, ImproveButton, AddToLibraryButton, SourcesButton, ActionButtonGroup } from "./actions";
import { CHAT_ANIMATION, ACTION_BUTTON_STYLE, MESSAGE_STYLES } from "./constants";
import type { ChatMessage, Evidence, MessageActionConfig, PromptSource } from "./types";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import type { Artefact } from "@/modules/chat/chat-ui.types";
import type { WebSearchSource } from "@/types/open-chat";

interface Props {
  message: ChatMessage;
  evidences?: Evidence[];
  isStreaming?: boolean;
  actions?: MessageActionConfig;
  source?: PromptSource;
  onImprovePrompt?: () => void;
  onArtefactClick?: (artefactId: string) => void;
  inlineArtefacts?: Artefact[];
  webSearchSources?: WebSearchSource[];
  onWebCitationClick?: (sourceIds: number[], messageId?: string) => void;
  onSourcesButtonClick?: (messageId?: string) => void;
  hideActions?: boolean;
  loaderClassName?: string;
  loaderLabel?: string;
  loaderAlign?: React.ComponentProps<typeof TypingIndicator>["align"];
}

export const ChatMessageBubble = React.memo(
  ({
    message,
    evidences = [],
    isStreaming = false,
    actions = {},
    source = "open_chat",
    onImprovePrompt,
    onArtefactClick,
    inlineArtefacts,
    webSearchSources,
    onWebCitationClick,
    onSourcesButtonClick,
    hideActions = false,
    loaderClassName,
    loaderLabel,
    loaderAlign,
  }: Props) => {
    const contentRef = React.useRef<HTMLDivElement>(null);
    const { formatTime, formatDateTime } = useDateFormatter();
    const isUser = message.type === "human";
    const isCompleted = message.completed !== false;
    const showActions = !hideActions && isCompleted && !isStreaming;

    const hasUserActions = actions.showImprove || actions.showAddToLibrary;
    const hasAiActions = actions.showCopy || actions.showSources;

    return (
      <motion.div
        className="mb-3 w-full"
        initial={CHAT_ANIMATION.fadeIn}
        animate={CHAT_ANIMATION.visible}
        exit={CHAT_ANIMATION.fadeOut}
        transition={CHAT_ANIMATION.spring}
      >
        <div className={isUser ? MESSAGE_STYLES.user : MESSAGE_STYLES.ai}>
          <div ref={contentRef}>
            <MarkdownRenderer
              content={message.content}
              evidences={evidences}
              processCitations={evidences.length > 0 || !!webSearchSources?.length}
              onArtefactClick={onArtefactClick}
              inlineArtefacts={inlineArtefacts}
              webSearchSources={webSearchSources}
              onWebCitationClick={
                onWebCitationClick ? (sourceIds: number[]) => onWebCitationClick(sourceIds, message.id) : undefined
              }
              isStreaming={isStreaming}
            />
          </div>
          <AnimatePresence>
            {isStreaming && (
              <div className={loaderClassName}>
                <TypingIndicator align={loaderAlign} label={loaderLabel} />
              </div>
            )}
          </AnimatePresence>
        </div>
        {message.timestamp && !isStreaming && (
          <div className={`mt-1 text-xs text-muted-foreground ${isUser ? "text-right" : "pl-3 text-left"}`}>
            {isToday(message.timestamp) ? formatTime(message.timestamp) : formatDateTime(message.timestamp)}
          </div>
        )}

        {showActions && isUser && hasUserActions && (
          <ActionButtonGroup>
            {actions.showImprove && <ImproveButton content={message.content} onImprovePrompt={onImprovePrompt} />}
            {actions.showAddToLibrary && <AddToLibraryButton content={message.content} source={source} />}
          </ActionButtonGroup>
        )}

        {showActions && !isUser && hasAiActions && (
          <ActionButtonGroup>
            {actions.showSources &&
              (webSearchSources?.length && onSourcesButtonClick ? (
                <button className={ACTION_BUTTON_STYLE} onClick={() => onSourcesButtonClick?.(message.id)}>
                  <Globe size={14} className="mr-1" />
                  Sources
                </button>
              ) : (
                <SourcesButton content={message.content} />
              ))}
            {actions.showCopy && (
              <CopyButton content={message.content} message={message} evidences={evidences} contentRef={contentRef} />
            )}
          </ActionButtonGroup>
        )}
      </motion.div>
    );
  },
);
